import { useEffect, useState } from "react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";

// Storefront renderer design tokens — must come BEFORE dashboard styles
// so dashboard variables.scss overrides any conflicting :root vars (--color-border, --radius)
import "./index.scss";
// suneditor's CSS is imported by the Editor component (shared/ui/common/form/
// editor/Editor.tsx), not here — it's ~20 kB of render-blocking CSS that only
// rich-text-editor screens need.

import App from "./App.tsx";
import { tokenService } from "@/shared/utils/tokenService";
import { setAuth0TokenGetter } from "@/shared/api";
import { installChunkReloadHandler } from "@/shared/lib/chunk-reload";

// Deploy-skew recovery: a tab opened before a deploy references old hashed
// /assets chunks that no longer exist; its next lazy import fails and the
// route crashes. Reload once (guarded) to pick up the new index.html.
installChunkReloadHandler();

// 🗣️ i18n imports
import i18n, { syncHtmlLang, normalize } from "./i18n";
import { detectDefaultLocaleFromGeo } from "./locale-detect.ts";

// ✅ Ensure Auth0 environment variables are defined
const requiredAuth0EnvVars = [
  "VITE_AUTH0_DOMAIN",
  "VITE_AUTH0_CLIENT_ID",
  "VITE_AUTH0_AUDIENCE",
  "VITE_AUTH0_SCOPE",
] as const;

const missingAuth0EnvVars = requiredAuth0EnvVars.filter(
  (key) => !(import.meta.env as Record<string, string | undefined>)[key],
);

if (missingAuth0EnvVars.length > 0) {
  throw new Error(
    `Missing Auth0 environment variable(s): ${missingAuth0EnvVars.join(", ")}`,
  );
}

// 🔐 Auth0 configuration
const auth0Config = {
  domain: import.meta.env.VITE_AUTH0_DOMAIN!,
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID!,
  authorizationParams: {
    redirect_uri: window.location.origin,
    audience: import.meta.env.VITE_AUTH0_AUDIENCE,
    scope: `${import.meta.env.VITE_AUTH0_SCOPE || "openid profile email"} offline_access`,
    store: "admin",
    prompt: "login",
  },
  onRedirectCallback: (appState?: { returnTo?: string }) => {
    const target = appState?.returnTo || window.location.pathname;
    window.history.replaceState({}, document.title, target);
  },
};

// 🔄 Token Manager Component - Listens to Auth0 token changes and stores them
function TokenManager({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, getAccessTokenSilently, logout, user, isLoading } =
    useAuth0();

  // Register Auth0's token getter for the axios auth interceptor HERE, during
  // render, rather than from a useEffect further down the tree.
  //
  // TokenManager is the first component inside Auth0Provider and wraps <App />,
  // so this runs before CurrencyProvider / PolicyAcceptanceProvider — both of
  // which call useMyProfile() and would otherwise start GET /auth/profile with
  // no getter registered, producing a 401 followed by the retry interceptor's
  // replay on every cold load.
  //
  // Assigning a module-level slot is idempotent, so re-running it on each
  // render (including StrictMode's double render) is harmless.
  setAuth0TokenGetter(getAccessTokenSilently);

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      // User is not authenticated and Auth0 has finished loading
      // Clear token and namespace when user logs out
      tokenService.removeAccessToken();
      tokenService.removeApiNamespace();
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    // Get and store initial token + determine API namespace
    const storeInitialToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        if (token) {
          tokenService.setAccessToken(token);

          // Determine API namespace from Auth0 user claims
          // Check for isSuperAdmin claim in the user object
          const isSuperAdmin =
            user?.["https://api.storemandu.com/isSuperAdmin"] === true ||
            (user as any)?.isSuperAdmin === true;

          const namespace = isSuperAdmin ? "system" : "merchant";
          tokenService.setApiNamespace(namespace);

          if (import.meta.env.DEV) console.log(`API Namespace set to: ${namespace}`); // eslint-disable-line no-console
        }
      } catch (error) {
        if (import.meta.env.DEV) console.error("Error getting initial token:", error); // eslint-disable-line no-console
        // If we can't get a token, clear everything and redirect to login
        tokenService.removeAccessToken();
        tokenService.removeApiNamespace();
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      }
    };

    storeInitialToken();
  }, [isAuthenticated, isLoading, getAccessTokenSilently, logout, user]);

  // Check if localStorage is cleared while on a protected route. Driven by
  // events instead of the old 2s setInterval (which kept the CPU waking up
  // forever): the `storage` event covers other tabs (logout elsewhere);
  // focus/visibilitychange cover this tab after the user returns from
  // dev-tools or another window.
  useEffect(() => {
    const checkAuth = () => {
      // Only check if we're on a protected route (contains /store/)
      if (window.location.pathname.includes("/store/")) {
        // Check if Auth0 tokens exist in localStorage
        const auth0Keys = Object.keys(localStorage).filter(
          (key) => key.startsWith("@@auth0spajs@@") || key.startsWith("auth0"),
        );

        // If no Auth0 tokens found and we're not authenticated, redirect to login
        if (auth0Keys.length === 0 && !isAuthenticated && !isLoading) {
          if (import.meta.env.DEV) console.log("No Auth0 tokens found, redirecting to login"); // eslint-disable-line no-console
          tokenService.removeAccessToken();
          tokenService.removeApiNamespace();
          window.location.href = "/";
        }
      }
    };

    // Check immediately
    checkAuth();

    const onVisible = () => {
      if (!document.hidden) checkAuth();
    };
    window.addEventListener("storage", checkAuth);
    window.addEventListener("focus", checkAuth);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("storage", checkAuth);
      window.removeEventListener("focus", checkAuth);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isAuthenticated, isLoading]);

  return <>{children}</>;
}

// 🌐 Root component with i18n initialization
function Root() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const cached = localStorage.getItem("i18nextLng");

    if (cached) {
      const lng = normalize(cached);
      syncHtmlLang(lng);
      // Every language (en included) is a lazy chunk now (see src/i18n.ts).
      // Load + switch before first render so the user never sees raw keys —
      // it's a small same-origin chunk, not a third-party API call.
      i18n
        .changeLanguage(lng)
        .catch(() => {
          /* chunk failed to load — i18next falls back as far as it can */
        })
        .finally(() => setReady(true));
      return;
    }

    // First visit (no cached language): render as soon as the default (en)
    // locale chunk lands and let geo detection upgrade the language in the
    // background. The chunk is same-origin and small (~35 kB br) — unlike the
    // old geolocation-prompt + IP-API gate (up to ~4.5s), this is one fetch
    // that usually races the Auth0 bootstrap anyway.
    syncHtmlLang(i18n.language);
    i18n
      .loadLanguages("en")
      .catch(() => {
        /* offline — render anyway; keys degrade gracefully */
      })
      .finally(() => setReady(true));
    void (async () => {
      try {
        const geoLocale = await detectDefaultLocaleFromGeo();
        const supported = normalize(geoLocale);
        if (supported !== normalize(i18n.language)) {
          // changeLanguage loads the locale chunk and re-renders; the
          // languageChanged listener in i18n.ts keeps <html lang> in sync.
          await i18n.changeLanguage(supported);
        }
      } catch {
        /* stay on the default locale */
      }
    })();
  }, []);

  if (!ready) return null;

  return (
    <StrictMode>
      <Auth0Provider
        {...auth0Config}
        useRefreshTokens
        useRefreshTokensFallback={false}
        cacheLocation="localstorage"
      >
        <TokenManager>
          <App />
        </TokenManager>
      </Auth0Provider>
    </StrictMode>
  );
}

createRoot(document.getElementById("root")!).render(<Root />);

// Warm the app-shell chunks (Root → guard → layout → dashboard) during idle
// time. Route-level code-splitting makes the first navigation a multi-hop
// dynamic-import waterfall; for a user with an Auth0 session this fetches
// the hops in parallel right after first paint instead. Skipped for
// anonymous visitors — no point speculating past the login page. The import
// paths must mirror src/app/router/imports.tsx so they resolve to the same
// chunks (same module = same chunk = cache hit, never a double download).
const hasAuth0Session = Object.keys(localStorage).some((k) =>
  k.startsWith("@@auth0spajs@@"),
);
if (hasAuth0Session) {
  const warmup = () => {
    void import("@/widgets/app-layout/Root");
    void import("@/app/router/guards/RequirePermission");
    void import("@/widgets/app-layout/Layout");
    void import("@/widgets/dashboard/Dashboard");
  };
  if ("requestIdleCallback" in window) {
    requestIdleCallback(warmup, { timeout: 3000 });
  } else {
    setTimeout(warmup, 1500);
  }
}
